import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogOpenChangeData,
  DialogOpenChangeEvent,
} from '@fluentui/react-components';
import { useFluentTokens } from '../../../theme/fluent';
import { dialogSizes, DialogSize } from '../../../config/dialog';

interface ScrollableDialogProps {
  open: boolean;
  onOpenChange: (event: DialogOpenChangeEvent, data: DialogOpenChangeData) => void;
  title: React.ReactNode;
  titleAction?: JSX.Element;
  children: React.ReactNode;
  actionsLeft?: React.ReactNode;
  actionsRight?: React.ReactNode;
  size?: DialogSize;
  modalType?: 'modal' | 'non-modal' | 'alert';
  scrollableContentLabel?: string;
  contentStyle?: React.CSSProperties;
}

/**
 * ScrollableDialog - A Dialog wrapper that ensures proper scrolling behavior
 *
 * Key features:
 * - Respects maxHeight constraint from dialogSizes
 * - DialogContent uses flex: 1 to fill available space within the dialog
 * - DialogContent scrolls when content exceeds available space
 * - DialogTitle and DialogActions remain fixed (not scrollable)
 * - Works correctly on small browser windows by using flexbox layout
 * - Close button properly positioned in top-right
 *
 * Usage:
 * ```tsx
 * <ScrollableDialog
 *   open={open}
 *   onOpenChange={(_, data) => !data.open && onClose()}
 *   title={<Text size={500}>My Dialog</Text>}
 *   titleAction={<Button appearance="subtle" icon={<Dismiss24Regular />} onClick={onClose} />}
 *   actions={<Button onClick={onSave}>Save</Button>}
 *   size="large"
 * >
 *   <Box>Your content here</Box>
 * </ScrollableDialog>
 * ```
 */
export const ScrollableDialog: React.FC<ScrollableDialogProps> = ({
  open,
  onOpenChange,
  title,
  titleAction,
  children,
  actionsLeft,
  actionsRight,
  size = 'large',
  modalType = 'modal',
  scrollableContentLabel = 'Dialog content',
  contentStyle,
}) => {
  const sizeConfig = dialogSizes[size];
  const { tokens } = useFluentTokens();

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modalType={modalType}>
      <DialogSurface style={sizeConfig}>
        <DialogBody>
          <DialogTitle action={titleAction}>{title}</DialogTitle>

          <DialogContent
            tabIndex={0}
            role="region"
            aria-label={scrollableContentLabel}
            style={{
              overflowY: 'auto',
              maxHeight: `calc(${sizeConfig.maxHeight} - 150px)`,
              outlineOffset: '-2px',
              borderRadius: tokens.borderRadiusMedium,
              ...contentStyle,
            }}
          >
            {children}
          </DialogContent>

          {actionsLeft && <DialogActions position="start">{actionsLeft}</DialogActions>}
          {actionsRight && <DialogActions position="end">{actionsRight}</DialogActions>}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
